import React, { useEffect, useMemo, useRef, useState } from "react";
import { Mic, Square, Pause, Play, Trash2, Loader2, Save, RefreshCw, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useLang, translateFr as tfr } from "@/i18n";
import useVoiceRecorder from "./useVoiceRecorder";

export const NOTE_CATEGORIES = ["Général", "Réunion", "Appel", "Médical", "Contrat", "Autre"];
const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

const ERRORS = {
  denied: "Accès au microphone refusé",
  nodevice: "Aucun microphone détecté",
  unsupported: "Enregistrement non pris en charge par ce navigateur",
  empty: "Enregistrement vide — aucun son capté",
};

export function Waveform({ levels, active }) {
  const bars = levels.length ? levels : Array(32).fill(0.05);
  return (
    <div className="flex items-center gap-[2px] h-9 flex-1 min-w-0 overflow-hidden" aria-hidden data-testid="voice-waveform">
      {bars.slice(-48).map((v, i) => (
        <span key={i} className={cn("w-[3px] rounded-full shrink-0 transition-all duration-100",
          active ? "bg-athlynx" : "bg-muted-foreground/40")}
          style={{ height: `${Math.max(8, v * 100)}%` }} />
      ))}
    </div>
  );
}

export default function VoiceRecorderPanel({ athlete, onSaved, onClose, autoStart = false }) {
  const { tf } = useLang();
  const rec = useVoiceRecorder();
  const [transcript, setTranscript] = useState("");
  const [txState, setTxState] = useState("idle"); // idle|loading|done|error
  const [txError, setTxError] = useState("");
  const [language, setLanguage] = useState(null);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("Général");
  const [athleteId, setAthleteId] = useState(athlete?.id || "");
  const [players, setPlayers] = useState(null);
  const [saving, setSaving] = useState(false);
  const [keepAudio, setKeepAudio] = useState(false);
  const [retentionDays, setRetentionDays] = useState("30");
  const clientToken = useMemo(() => `vn_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`, []);
  const startedRef = useRef(false);
  const audioUrl = useMemo(() => (rec.blob ? URL.createObjectURL(rec.blob) : null), [rec.blob]);

  useEffect(() => () => { if (audioUrl) URL.revokeObjectURL(audioUrl); }, [audioUrl]);
  useEffect(() => {
    if (autoStart && !startedRef.current) { startedRef.current = true; rec.start(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart]);
  useEffect(() => {
    if (!athlete) {
      api.get("/players").then((r) => {
        const list = r.data?.items || r.data?.players || (Array.isArray(r.data) ? r.data : []);
        setPlayers(list.map((p) => ({ id: p.id, name: p.name })));
      }).catch(() => setPlayers([]));
    }
  }, [athlete]);

  const transcribe = async (blob) => {
    setTxState("loading"); setTxError("");
    try {
      const form = new FormData();
      const ext = (blob.type || "").includes("mp4") ? "m4a.mp4" : "note.webm";
      form.append("file", blob, ext);
      const r = await api.post("/voice/transcribe", form, { headers: { "Content-Type": "multipart/form-data" }, timeout: 90000 });
      setTranscript(r.data.text || "");
      setLanguage(r.data.language || null);
      setTxState("done");
    } catch (e) {
      setTxError(e?.response?.data?.detail || tf("Service de transcription indisponible — réessayez"));
      setTxState("error");
    }
  };

  useEffect(() => {
    if (rec.state === "stopped" && rec.blob && txState === "idle") transcribe(rec.blob);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rec.state, rec.blob]);

  const discard = () => { rec.cancel(); setTranscript(""); setTxState("idle"); setTitle(""); onClose?.(); };

  const save = async () => {
    if (saving || !transcript.trim() || !athleteId) return;
    setSaving(true);
    try {
      const r = await api.post(`/players/${athleteId}/notes`, {
        title: title.trim() || tf("Note vocale"),
        category, content: transcript.trim(), source_type: "voice",
        language, duration_seconds: rec.seconds, client_token: clientToken,
      });
      if (keepAudio && rec.blob && r.data?.note?.id) {
        try {
          const form = new FormData();
          form.append("file", rec.blob, (rec.blob.type || "").includes("mp4") ? "a.mp4" : "a.webm");
          form.append("retention_days", retentionDays);
          await api.post(`/players/notes/${r.data.note.id}/audio`, form,
            { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
        } catch {
          toast.warning(tf("Note enregistrée, mais l'audio n'a pas pu être conservé."));
        }
      }
      toast.success(tf("Note enregistrée"));
      onSaved?.(athleteId);
      discard();
    } catch (e) {
      toast.error(e?.response?.data?.detail || tf("Échec de l'enregistrement de la note"));
    } finally { setSaving(false); }
  };

  const recording = rec.state === "recording" || rec.state === "paused";

  return (
    <div className="space-y-3" data-testid="voice-recorder-panel">
      {rec.state === "error" && (
        <div className="rounded-lg border border-danger/30 bg-danger/[0.06] px-3 py-2 text-xs text-danger flex items-center gap-2" role="alert" data-testid="voice-error">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> {tf(ERRORS[rec.error] || "Erreur d'enregistrement")}
          <Button size="sm" variant="ghost" className="ml-auto h-6 text-[11px]" onClick={rec.start} data-testid="voice-retry-record">{tf("Réessayer")}</Button>
        </div>
      )}

      {(rec.state === "idle" || rec.state === "requesting") && (
        <Button onClick={rec.start} disabled={rec.state === "requesting"} className="w-full h-10 gap-2 bg-athlynx hover:bg-athlynx/90 text-white" data-testid="voice-start-btn">
          {rec.state === "requesting" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mic className="h-4 w-4" />}
          {tf("Démarrer l'enregistrement")}
        </Button>
      )}

      {recording && (
        <div className="rounded-xl border border-border bg-secondary/40 p-3 space-y-2" data-testid="voice-recording-box">
          <div className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-athlynx animate-pulse" aria-hidden />
            <span className="text-xs font-semibold text-athlynx">{rec.state === "paused" ? tf("En pause") : tf("Enregistrement…")}</span>
            <span className="text-xs font-mono text-muted-foreground" data-testid="voice-duration">{fmt(rec.seconds)} / 03:00</span>
          </div>
          <div className="flex items-center gap-2">
            <Waveform levels={rec.levels} active={rec.state === "recording"} />
            {rec.canPause && (rec.state === "recording" ? (
              <Button size="icon" variant="outline" className="h-9 w-9 shrink-0" onClick={rec.pause} aria-label={tf("Pause")} data-testid="voice-pause-btn"><Pause className="h-4 w-4" /></Button>
            ) : (
              <Button size="icon" variant="outline" className="h-9 w-9 shrink-0" onClick={rec.resume} aria-label={tf("Reprendre")} data-testid="voice-resume-btn"><Play className="h-4 w-4" /></Button>
            ))}
            <Button size="icon" className="h-9 w-9 shrink-0 bg-athlynx hover:bg-athlynx/90 text-white" onClick={rec.stop} aria-label={tf("Arrêter l'enregistrement")} data-testid="voice-stop-btn"><Square className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" className="h-9 w-9 shrink-0 text-muted-foreground" onClick={() => { rec.cancel(); onClose?.(); }} aria-label={tf("Annuler")} data-testid="voice-cancel-btn"><Trash2 className="h-4 w-4" /></Button>
          </div>
          <p className="text-[10px] text-muted-foreground/80 leading-snug">
            {tf("Veillez à respecter les règles applicables si vous enregistrez une conversation impliquant d'autres personnes.")}
          </p>
        </div>
      )}

      {rec.state === "stopped" && (
        <div className="space-y-3" data-testid="voice-review-box">
          {rec.maxReached && (
            <div className="rounded-lg border border-warning/30 bg-warning/[0.07] px-3 py-2 text-xs text-warning" data-testid="voice-max-reached">
              {tf("Durée maximale atteinte (3 minutes) — l'enregistrement a été arrêté automatiquement.")}
            </div>
          )}
          {audioUrl && <audio controls src={audioUrl} className="w-full h-9" data-testid="voice-playback" />}
          {txState === "loading" && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="voice-transcribing">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-info" /> {tf("Transcription en cours")}
            </div>
          )}
          {txState === "error" && (
            <div className="rounded-lg border border-danger/30 bg-danger/[0.06] px-3 py-2 text-xs text-danger flex items-center gap-2" role="alert" data-testid="voice-tx-error">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> {txError}
              <Button size="sm" variant="ghost" className="ml-auto h-6 text-[11px] gap-1" onClick={() => transcribe(rec.blob)} data-testid="voice-tx-retry">
                <RefreshCw className="h-3 w-3" /> {tf("Réessayer")}
              </Button>
            </div>
          )}
          {(txState === "done" || txState === "error") && (
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="voice-transcript">{tf("Transcription (modifiable)")}</label>
              <Textarea id="voice-transcript" value={transcript} onChange={(e) => setTranscript(e.target.value)}
                className="min-h-[90px] text-sm" placeholder={txState === "error" ? tf("Saisissez votre note manuellement…") : ""}
                data-testid="voice-transcript-input" />
            </div>
          )}
          {!athlete ? (
            <div className="space-y-1">
              <label className="text-xs font-medium">{tf("Associer à un athlète")}</label>
              <Select value={athleteId} onValueChange={setAthleteId}>
                <SelectTrigger className="h-9" data-testid="voice-athlete-select"><SelectValue placeholder={tf("Choisir un athlète")} /></SelectTrigger>
                <SelectContent>
                  {(players || []).map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`voice-athlete-${p.id}`}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="text-xs text-muted-foreground" data-testid="voice-athlete-locked">
              {tf("Athlète")} : <span className="font-medium text-foreground">{athlete.name}</span>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={tf("Titre de la note")}
              className="h-9 text-sm" data-testid="voice-title-input" />
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="h-9" data-testid="voice-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {NOTE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{tfr(c)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="rounded-lg border border-border p-2.5 space-y-2" data-testid="voice-keep-audio-row">
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" checked={keepAudio} onChange={(e) => setKeepAudio(e.target.checked)}
                className="mt-0.5 h-3.5 w-3.5 accent-current" data-testid="voice-keep-audio-checkbox" />
              <span className="text-xs">
                <span className="font-medium">{tf("Conserver l'enregistrement audio")}</span>
                <span className="block text-muted-foreground mt-0.5" data-testid="voice-save-mode">
                  {keepAudio
                    ? tf("Votre enregistrement original sera supprimé en toute sécurité après la période sélectionnée. Votre transcription restera disponible.")
                    : tf("Seule la transcription sera conservée. L'enregistrement original sera supprimé.")}
                </span>
              </span>
            </label>
            {keepAudio && (
              <div className="flex items-center gap-2 pl-[22px]" data-testid="voice-retention-row">
                <span className="text-xs text-muted-foreground shrink-0">{tf("Supprimer automatiquement après")}</span>
                <Select value={retentionDays} onValueChange={setRetentionDays}>
                  <SelectTrigger className="h-8 w-[110px] text-xs" aria-label={tf("Supprimer automatiquement après")}
                    data-testid="voice-retention-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30" data-testid="voice-retention-30">{tf("30 jours")}</SelectItem>
                    <SelectItem value="90" data-testid="voice-retention-90">{tf("90 jours")}</SelectItem>
                    <SelectItem value="365" data-testid="voice-retention-365">{tf("365 jours")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={discard} className="text-muted-foreground" data-testid="voice-discard-btn">{tf("Abandonner")}</Button>
            <Button onClick={save} disabled={saving || !transcript.trim() || !athleteId}
              className="ml-auto gap-2 bg-info hover:bg-info/90 text-white" data-testid="voice-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {tf("Enregistrer dans les notes du joueur")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
